import { sleepRetry } from './shared';

// --- Interfaces for better JSDoc typing ---
/**
 * @typedef {Object} BCSuiteAccountStoreEntitySiteEntityData
 * @property {string} id - The site ID.
 */

/**
 * @typedef {Object} BCSuiteAccountStoreEntitySiteEntity
 * @property {BCSuiteAccountStoreEntitySiteEntityData} data
 */

/**
 * @typedef {Object} BCSuiteAccountStoreEntityData
 * @property {string} id - The store ID.
 */

/**
 * @typedef {Object} BCSuiteAccountStoreEntity
 * @property {BCSuiteAccountStoreEntitySiteEntity} siteEntity
 * @property {BCSuiteAccountStoreEntityData} data
 */

/**
 * @typedef {Object} BCSuiteAccount
 * @property {BCSuiteAccountStoreEntity} storeEntity
 */

/**
 * Represents the BCSuite API client.
 * (This is a simplified representation; adapt to your actual BCSuite structure)
 * @typedef {Object} BCSuiteAPI
 * @property {BCSuiteAccount | null} account - Current account information.
 * @property {() => Promise<BCSuiteAccount>} getAccount - Fetches account data.
 * @property {(resource: string, params: object, query: object) => Promise<BCSuiteResponse>} getResourceData - Fetches data for a resource.
 * @property {(resource: string, body: object, params: object) => Promise<BCSuiteResponse>} postResourceData - Posts data to a resource.
 * @property {(userType: string) => Promise<object>} logoutUser - Logs out a user.
 * @property {BCSuiteManagerAPI} Manager - Manager for cart operations.
 */

/**
 * @typedef {Object} BCSuiteManagerAPI
 * @property {(cartId: string) => Promise<object>} deleteCart
 * @property {() => Promise<object>} getCartDetails
 * @property {() => Promise<object>} getCart
 */

/**
 * @typedef {Object} BCSuiteStatus
 * @property {number} code - HTTP status code.
 * @property {string} [message] - Optional status message.
 */

/**
 * @typedef {Object} BCSuiteResponse
 * @property {any} data - The response data.
 * @property {BCSuiteStatus} status - The response status.
 * @property {string} message - A descriptive message.
 */

/**
 * Represents a Login class instance.
 * (This is a simplified representation)
 * @typedef {Object} LoginInstance
 * @property {() => void} open - Opens the login modal/dialog.
 */


// --- CommonService ---
/**
 * Contains shared low-level communication and utility methods.
 * This class is not meant to be instantiated directly by consumers.
 */
class CommonService {
    /**
     * @param {BCSuiteAPI} BCSuiteInstance - The BCSuite API client instance.
     */
    constructor(BCSuiteInstance) {
        if (!BCSuiteInstance) {
            throw new Error("BCSuite instance is required for CommonService.");
        }
        /** @private @type {BCSuiteAPI} */
        this.BCSuite = BCSuiteInstance;
        /** @private @type {{siteId: string, storeId: string} | null} */
        this._accountData = null;
    }

    /**
     * Retrieves site and store parameters for BCSuite API calls.
     * Caches the result for subsequent calls.
     * @async
     * @returns {Promise<{siteId: string | null, storeId: string | null}>}
     */
    async getParamsForBCSuite() {
        try {
            if (!this.BCSuite.account) {
                await sleepRetry(() => !!this.BCSuite.account, 5000, 500);
            }
            if (!this.BCSuite.account) {
                // console.error("BCSuite.account not available after retry");
                return { siteId: null, storeId: null };
            }

            const accountData = await this.BCSuite.getAccount();
            if (
                this._accountData ||
                (accountData &&
                    accountData.storeEntity &&
                    accountData.storeEntity.siteEntity)
            ) {
                if (!this._accountData && accountData && accountData.storeEntity && accountData.storeEntity.siteEntity) {
                     this._accountData = {
                        siteId: accountData.storeEntity.siteEntity.data.id,
                        storeId: accountData.storeEntity.data.id,
                    };
                } else if (this._accountData) {
                    // use cached
                } else {
                    // console.error("Account data structure is not as expected:", accountData);
                    return { siteId: null, storeId: null };
                }
                return this._accountData;
            }
            // console.warn("Could not retrieve valid accountData for BCSuite params");
            return { siteId: null, storeId: null };
        } catch (e) {
            // console.error('Error in getParamsForBCSuite:', e);
            return { siteId: null, storeId: null };
        }
    }

    /**
     * Low-level GET request to BCSuite.
     * @private
     * @async
     * @param {string} resource - The API resource path.
     * @param {object | null} params - Path or request parameters.
     * @param {object | null} query - Query string parameters.
     * @returns {Promise<BCSuiteResponse>} The raw response from BCSuite.
     */
    async _baseGet(resource, params, query) {
        const { siteId, storeId } = await this.getParamsForBCSuite();
        if ((resource.startsWith('os_') || resource.startsWith('oc_')) && (siteId === null || storeId === null)) {
            // console.error(`Cannot make GET request to ${resource} without siteId/storeId.`);
            return { data: null, status: { code: 400, message: "Missing site/store context" }, message: "Configuration error" };
        }
        return this.BCSuite.getResourceData(
            resource,
            { siteId, storeId, ...params },
            { ...query }
        );
    }

    /**
     * Low-level POST request to BCSuite.
     * @private
     * @async
     * @param {string} resource - The API resource path.
     * @param {object | null} params - Path or request parameters.
     * @param {object} body - The request body.
     * @param {object | null} [query] - Query string parameters.
     * @returns {Promise<BCSuiteResponse>} The raw response from BCSuite.
     */
    async _basePost(resource, params, body, query) {
        const { siteId, storeId } = await this.getParamsForBCSuite();
        if (resource.startsWith('os_') && (siteId === null || storeId === null)) {
            // console.error(`Cannot make POST request to ${resource} without siteId/storeId.`);
            return { data: null, status: { code: 400, message: "Missing site/store context" }, message: "Configuration error" };
        }
        return this.BCSuite.postResourceData(
            resource,
            body,
            { siteId, storeId, ...params },
            { ...query }
        );
    }

    /**
     * Deletes a cart.
     * @async
     * @param {string} cartId - The ID of the cart to delete.
     * @returns {Promise<object>} The result of the delete operation.
     */
    async deleteCart(cartId) {
        return await this.BCSuite.Manager.deleteCart(cartId);
    }

    /**
     * Gets details of the current cart.
     * @async
     * @returns {Promise<object>} Cart details.
     */
    async getCartDetails() {
        return await this.BCSuite.Manager.getCartDetails();
    }

    /**
     * Gets the current cart.
     * @async
     * @returns {Promise<object>} The current cart.
     */
    async getCart() {
        return await this.BCSuite.Manager.getCart();
    }

    /**
     * Gets the current session
     * @returns {Object} The Session state
     */
    getSession() {
        return this.BCSuite.getSession('os');
    }

    async recalculateCart(cartId, version) {
        return await this._baseGet('os_recalculate_cart', { cartId, version });
    }

    /**
     * Get the base url of the service
     * @returns string
     */
    getBaseUrl = () => BCSuite.bcsUrl;
}

// --- SellerService ---
/**
 * Service class for seller-specific operations.
 * Manages authorization state and interacts with the login UI.
 * This class is not meant to be instantiated directly by consumers.
 */
class SellerService {
    /**
     * @param {CommonService} commonService - Instance of CommonService.
     * @param {LoginInstance} loginInstance - Instance of the Login UI handler.
     */
    constructor(commonService, loginInstance) {
        if (!commonService || !loginInstance) {
            throw new Error("CommonService and Login instance are required for SellerService.");
        }
        /** @private @type {CommonService} */
        this.commonService = commonService;
        /** @private @type {LoginInstance} */
        this.login = loginInstance;
        /** @private @type {boolean} */
        this._authorized = false;
    }

    /**
     * Processes the raw response from BCSuite for seller endpoints.
     * Handles authorization and login notifications.
     * @private
     * @param {BCSuiteResponse} rawResponse - The raw response from BCSuite.
     * @param {boolean} loginNotification - Whether to show login notification on 403.
     * @returns {{data: any, message: string, status: number}} Processed response.
     */
    _processSellerResponse(rawResponse, loginNotification) {
        const { data, status, message } = rawResponse;
        if (!status) {
            this._authorized = false;
            return { data: null, message: "Invalid response from server", status: 500 };
        }

        if (status.code === 200 || status.code === 0) {
            this._authorized = true;
            return { data, message: '', status: status.code };
        } else if (status.code === 403) {
            this._authorized = false;
            if (loginNotification && this.login) {
                this.login.open();
            }
            return { data: '', message, status: status.code };
        } else {
            return { data: '', message, status: status.code };
        }
    }

    /**
     * Performs a GET request for seller endpoints.
     * @private
     * @async
     * @param {string} resource - The API resource path.
     * @param {object | null} params - Path or request parameters.
     * @param {object | null} query - Query string parameters.
     * @param {boolean} [loginNotification=true] - Whether to show login notification on 403.
     * @returns {Promise<{data: any, message: string, status: number}>} Processed response.
     */
    async _sellerGet(resource, params, query, loginNotification = true) {
        const rawResponse = await this.commonService._baseGet(resource, params, query);
        return this._processSellerResponse(rawResponse, loginNotification);
    }

    /**
     * Performs a POST request for seller endpoints.
     * @private
     * @async
     * @param {string} resource - The API resource path.
     * @param {object | null} params - Path or request parameters.
     * @param {object} body - The request body.
     * @param {object | null} [query] - Query string parameters.
     * @param {boolean} [loginNotification=true] - Whether to show login notification on 403.
     * @returns {Promise<{data: any, message: string, status: number}>} Processed response.
     */
    async _sellerPost(resource, params, body, query, loginNotification = true) {
        const rawResponse = await this.commonService._basePost(resource, params, body, query);
        return this._processSellerResponse(rawResponse, loginNotification);
    }
    
    /**
     * Checks if the seller is currently authorized.
     * @type {boolean}
     */
    get isAuthorized() {
        return this._authorized;
    }

    /**
     * Gets session data for the seller.
     * @async
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getSessionData() {
        return await this._sellerGet('os_get_session_data', null, null, false);
    }

    /**
     * Gets the overall service state.
     * @async
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getServiceState() {
        return await this._sellerGet('os_service_state');
    }

    /**
     * Toggles the seller's online/offline state.
     * @async
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async toggleSellerState() {
        return await this._sellerGet('os_toggle_seller_state');
    }

    /**
     * Logs out the current seller.
     * @async
     * @returns {Promise<object>} The result of the logout operation.
     */
    async logoutSeller() {
        const result = await this.commonService.BCSuite.logoutUser('omni_seller');
        this._authorized = false;
        return result;
    }

    /**
     * Gets a login URL for a specific customer.
     * @async
     * @returns {Promise<object>}
     */
    async getCustomerLoginUrl(customerId) {
        return await this._sellerGet('os_get_customer_login_url', { customerId });
    }

    /**
     * Gets a specific quote by its ID.
     * @async
     * @param {string} quoteId - The ID of the quote.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getQuote(quoteId) {
        return await this._sellerGet('os_get_quote', { quoteId });
    }

    /**
     * Gets a quote by its associated cart ID.
     * @async
     * @param {string} cartId - The ID of the cart.
     * @param {string | null} [tcc=null] - Temporary Cart Code, if applicable.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getQuoteByCartId(cartId, tcc = null) {
        let query = tcc ? { tcc } : null;
        return await this._sellerGet('os_get_quote_by_cart_id', { cartId }, query);
    }

    /**
     * Gets a list of quotes for a seller, with optional filtering.
     * @async
     * @param {string} sellerId - The ID of the seller.
     * @param {Object} [filters={}] - Filtering options.
     * @param {string} [filters.search] - Search term.
     * @param {string} [filters.order] - Sort order (e.g., 'ASC', 'DESC').
     * @param {string} [filters.state] - Quote state.
     * @param {boolean} [filters.win] - Filter by won quotes.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getQuotes(sellerId, { search, order, state, win } = {}) {
        return await this._sellerGet(
            'os_get_quotes',
            { sellerId },
            { search, order, state, win }
        );
    }

    /**
     * Saves a quote associated with a cart ID.
     * @async
     * @param {string} cartId - The ID of the cart.
     * @param {object} body - The quote data to save.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async saveQuoteByCartId(cartId, body) {
        return await this._sellerPost('os_save_quote', { cartId }, body);
    }

    /**
     * Gets the product name for a quote line item.
     * @async
     * @param {string} cartId - The cart ID.
     * @param {string} cartLineId - The cart line item ID.
     * @param {number} quote_price - The quoted price.
     * @param {number} [quantity=1] - The quantity.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getQuoteProductName(cartId, cartLineId, quote_price, quantity = 1) {
        return await this._sellerGet('os_get_quote_product_name', { cartId, cartLineId }, { quote_price, quantity });
    }

    /**
     * Searches for prospects.
     * @async
     * @param {string} query - The search query.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async searchProspects(query) {
        return await this._sellerGet('os_search_prospects', null, { query });
    }

    /**
     * Gets a specific prospect by ID.
     * @async
     * @param {string} prospectId - The ID of the prospect.
     * @param {boolean} [stats] - Whether to include statistics.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getProspect(prospectId, stats) {
        return await this._sellerGet(
            'os_get_prospect',
            { prospectId },
            stats && { include: 'stats' }
        );
    }

    /**
     * Saves prospect data.
     * @async
     * @param {object} body - The prospect data to save.
     * @param {object} params - The params of data to save.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async saveProspect(body, query) {
        return await this._sellerPost('os_save_prospect', null, body, query);
    }

    /**
     * Gets activities for a specific quote.
     * @async
     * @param {string} quoteId - The ID of the quote.
     * @param {'ASC' | 'DESC'} [order='DESC'] - Sort order for activities.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getActivities(quoteId, query) {
        return await this._sellerGet('os_get_quote_activities', { quoteId }, { ...query, order: query?.order || 'DESC' });
    }

    /**
     * Saves an activity for a quote.
     * @async
     * @param {string} quoteId - The ID of the quote.
     * @param {object} body - The activity data to save.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async saveActivity(quoteId, body) {
        return await this._sellerPost('os_save_quote_activity', { quoteId }, body);
    }

    /**
     * Gets a link to view the PDF version of a quote.
     * @async
     * @param {string} quoteId - The ID of the quote.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getPDFQuoteViewLink(quoteId) {
        return await this._sellerGet('os_get_pdf_quote_view_link', { quoteId });
    }

    /**
     * Initiates sharing a quote.
     * @async
     * @param {string} quoteId - The ID of the quote.
     * @param {Object} params - Additional parameters for sharing.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async shareQuote(quoteId, params) {
        return await this._sellerGet('os_share_quote', { quoteId }, params);
    }

    /**
     * Gets AI-suggested message for a quote.
     * @async
     * @param {string} quoteId - The ID of the quote.
     * @param {Object} query - Request query containing additional_instruction.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getSuggestedMessage(quoteId, query) {
        return await this._sellerGet('os_get_suggested_quote_message', { quoteId }, query);
    }

    /**
     * Updates a customer's cart using a Temporary Cart Code (TCC).
     * @async
     * @param {string} tcc - The Temporary Cart Code.
     * @param {string} cartId - The ID of the cart to update.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async updateCartWithTCC(tcc, cartId) {
        return await this._sellerGet('os_update_customer_cart_by_tcc', { tcc, cartId });
    }

    /**
     * Loads a cart using a Temporary Cart Code (TCC).
     * @async
     * @param {string} tcc - The Temporary Cart Code.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async loadCartByTCC(tcc) {
        return await this._sellerGet('os_load_cart_by_tcc', { tcc });
    }

    /**
     * Initiates a requote process for an existing quote.
     * @async
     * @param {string} quoteId - The ID of the quote to requote.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async requote(quoteId) {
        return await this._sellerGet('os_requote', { quoteId });
    }

    /**
     * Loads a customer's cart.
     * @async
     * @param {string} cartId - The cart ID.
     * @param {Object} options - Loading options.
     * @param {string} [options.mode] - Loading mode.
     * @param {string} [options.tcc] - Temporary Cart Code.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async loadCustomerCart(cartId, {mode, tcc}) {
        return await this._sellerGet('os_load_customer_cart', {cartId}, {tcc, mode});
    }

    /**
     * Loads a quote.
     * @async
     * @param {string} quoteId - The quote ID.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async loadQuote(quoteId) {
        return await this._sellerGet('os_load_quote', {quoteId});
    }

    /**
     * Adds an SKU to the cart.
     * @async
     * @param {string} cartId - The cart ID.
     * @param {string} sku - The SKU to add.
     * @param {string} [currency_code] - Optional currency code to set for the cart (e.g., "USD", "EUR").
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async addSku(cartId, sku, currency_code) {
        const query = { sku };
        if (currency_code) {
            query.currency_code = currency_code;
        }
        return await this._sellerGet('os_add_sku', {cartId}, query);
    }
}

// --- CustomerService ---
/**
 * Service class for customer-specific operations.
 * Does not handle authorization or login UI.
 * This class is not meant to be instantiated directly by consumers.
 */
class CustomerService {
    /**
     * @param {CommonService} commonService - Instance of CommonService.
     */
    constructor(commonService) {
        if (!commonService) {
            throw new Error("CommonService instance is required for CustomerService.");
        }
        /** @private @type {CommonService} */
        this.commonService = commonService;
    }

    /**
     * Processes the raw response from BCSuite for customer endpoints.
     * @private
     * @param {BCSuiteResponse} rawResponse - The raw response from BCSuite.
     * @returns {{data: any, message: string, status: number}} Processed response.
     */
    _processCustomerResponse(rawResponse) {
        const { data, status, message } = rawResponse;
         if (!status) {
            return { data: null, message: "Invalid response from server", status: 500 };
        }
        if (status.code === 200 || status.code === 0) {
            return { data, message: '', status: status.code };
        } else {
            return { data: '', message, status: status.code };
        }
    }

    /**
     * Performs a GET request for customer endpoints.
     * @private
     * @async
     * @param {string} resource - The API resource path.
     * @param {object | null} params - Path or request parameters.
     * @param {object | null} query - Query string parameters.
     * @returns {Promise<{data: any, message: string, status: number}>} Processed response.
     */
    async _customerGet(resource, params, query) {
        const rawResponse = await this.commonService._baseGet(resource, params, query);
        return this._processCustomerResponse(rawResponse);
    }

    /**
     * Gets the loader data for a quoted cart using a Temporary Cart Code (TCC).
     * @async
     * @param {string} tcc - The Temporary Cart Code.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getQuotedCartLoaderByTcc(tcc, {accepted}) {
        let params = null;
        if (accepted) {
            params = {
                accepted,
            }
        }
        return await this._customerGet('os_get_quoted_cart_loader_by_tcc', { tcc }, params);
    }

    /**
     * Gets the loader data for a quoted cart disclaimer using a Temporary Cart Code (TCC).
     * @async
     * @param {string} tcc - The Temporary Cart Code.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getQuotedCartDisclaimer(tcc) {
        return await this._customerGet('os_get_quoted_cart_disclaimer', { tcc }, null);
    }

    /**
     * Gets a message related to a quoted cart.
     * @async
     * @param {string} cartId - The cart ID.
     * @param {string} tcc - The Temporary Cart Code.
     * @param {string} version - The version identifier.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getQuotedCartMessage(cartId, tcc, version) {
        return await this._customerGet('os_get_quoted_cart_message', { cartId, tcc}, { version }, null, false);
    }

    /**
     * Gets the Temporary Cart Code (TCC) for a given cart.
     * @async
     * @param {string} cartId - The cart ID.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async getCartTcc(cartId) {
        return await this._customerGet('oc_get_cart_tcc', {cartId}, null);
    }

    /**
     * Post a webhook to OS.
     * For simplicity, store_id and site_id fields are added to the provided object
     * @async
     * @param {string} entity - The webhook entity.
     * @param {string} event - The webhook event.
     * @returns {Promise<{data: any, message: string, status: number}>}
     */
    async postWebhook(entity, event, data) {
        // For the time being, webhooks are sent directly without passing through BCFC
        const { siteId, storeId } = await this.commonService.getParamsForBCSuite();
        const url = `${this.commonService.getBaseUrl()}/rest/ahia_bc/v1/site/${siteId}/store/${storeId}/app/os/entity/${entity}/event/${event}/webhook`;
        data["store_id"] = storeId;
        data["site_id"] = siteId;

        return await fetch(url, {
            method: "post",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data)
        });
    }
}


// --- ApiService ---

/**
 * @typedef {('seller' | 'customer')} ServiceType
 */

/**
 * @template T
 * @typedef {T extends 'seller' ? SellerService & CommonService & { authorized: boolean } : CustomerService & CommonService} TypedApiService
 */

/**
 * Main API service class that provides an interface to seller or customer operations.
 * The available methods will differ based on the `serviceType` provided during instantiation.
 *
 * **For 'seller' type:** Includes all methods from {@link SellerService} and {@link CommonService},
 * plus an `authorized` boolean property.
 *
 * **For 'customer' type:** Includes methods from {@link CustomerService} and {@link CommonService}.
 * The `authorized` property will be undefined.
 */
class ApiService {
    /**
     * Creates an instance of ApiService.
     * @param {ServiceType} serviceType - The type of service to instantiate ('seller' or 'customer').
     * @param {LoginInstance | null} loginInstance - An instance of the Login UI handler. Required for 'seller' type.
     * @param {BCSuiteAPI} BCSuiteInstance - An instance of the BCSuite API client.
     */
    constructor(serviceType, loginInstance, BCSuiteInstance) {
        if (!BCSuiteInstance) {
            throw new Error("BCSuite instance is required.");
        }
        if (serviceType === 'seller' && !loginInstance) {
            throw new Error("Login instance is required for seller service type.");
        }

        /** @private @type {CommonService} */
        this.commonService = new CommonService(BCSuiteInstance);

        /** @private @type {SellerService | CustomerService} */
        this.typedService;

        // Expose CommonService methods that are directly used by the API consumer

        /**
         * Get the base url of the service
         * @function
         * @name ApiService#getBaseUrl
         * @returns string
         */
        this.getBaseUrl = (...args) => this.commonService.getBaseUrl(...args);
        /**
         * Retrieves site and store parameters for BCSuite API calls.
         * @async
         * @function
         * @name ApiService#getParamsForBCSuite
         * @returns {Promise<{siteId: string | null, storeId: string | null}>}
         */
        this.getParamsForBCSuite = (...args) => this.commonService.getParamsForBCSuite(...args);
        /**
         * Deletes a cart.
         * @async
         * @function
         * @name ApiService#deleteCart
         * @param {string} cartId - The ID of the cart to delete.
         * @returns {Promise<object>} The result of the delete operation.
         */
        this.deleteCart = (...args) => this.commonService.deleteCart(...args);
        /**
         * Gets details of the current cart.
         * @async
         * @function
         * @name ApiService#getCartDetails
         * @returns {Promise<object>} Cart details.
         */
        this.getCartDetails = (...args) => this.commonService.getCartDetails(...args);
        /**
         * Gets the current cart.
         * @async
         * @function
         * @name ApiService#getCart
         * @returns {Promise<object>} The current cart.
         */
        this.getCart = (...args) => this.commonService.getCart(...args);
        /**
         * Recalculate cart discounts.
         * @async
         * @function
         * @name ApiService#recalculateCart
         * @returns {Promise<object>} Info regarding the updated cart.
         */
        this.recalculateCart = (...args) => this.commonService.recalculateCart(...args);
        /**
         * Gets the current session
         * @returns {Object} The Session state
         */
        this.getSession = (...args) => this.commonService.getSession(...args);
        
        if (serviceType === 'seller') {
            this.typedService = new SellerService(this.commonService, /** @type {LoginInstance} */ (loginInstance));
            // Dynamically add SellerService methods to ApiService instance
            for (const methodName of Object.getOwnPropertyNames(SellerService.prototype)) {
                if (methodName !== 'constructor' && typeof this.typedService[methodName] === 'function') {
                    this[methodName] = (...args) => this.typedService[methodName](...args);
                }
            }
             /**
             * Checks if the seller is currently authorized. Only available for 'seller' service type.
             * @name ApiService#authorized
             * @type {boolean | undefined}
             */
            Object.defineProperty(this, 'authorized', {
                get: () => /** @type {SellerService} */ (this.typedService).isAuthorized,
                enumerable: true,
                configurable: true
            });

        } else if (serviceType === 'customer') {
            this.typedService = new CustomerService(this.commonService);
            // Dynamically add CustomerService methods to ApiService instance
             for (const methodName of Object.getOwnPropertyNames(CustomerService.prototype)) {
                if (methodName !== 'constructor' && typeof this.typedService[methodName] === 'function') {
                    this[methodName] = (...args) => this.typedService[methodName](...args);
                }
            }
        } else {
            throw new Error(`Invalid serviceType specified: "${serviceType}". Must be "seller" or "customer".`);
        }
    }
}

export default ApiService;
